/**
 * The ink panels of the panel cutscenes (C0 the legend, C1 the blood pact; data/scenes.ts): flat ink
 * on rice paper, the one place the game is deliberately not lit like real material, but still hard
 * pixels with no smoothing. Figures are the game's own rig frames filled with ink (silhouettes).
 *
 * Drawn at `PANEL_W` × `PANEL_H` art pixels and scaled up by whole pixels on screen. Phase A of
 * docs/archive/OPENING.md: plain silhouettes on paper, no ink-bleed transition yet.
 */
import { artH, artW, artX, artY, canvas, ctx2d } from './pixel';
import type { SpriteSet } from './sprites';

export const PANEL_W = 192;
export const PANEL_H = 108;

/** Every panel art name the scenes use; a name not here draws the bare paper. */
export const PANEL_ARTS: readonly string[] = [
  'legend-ink',
  'legend-waves',
  'legend-bandits',
  'legend-swords',
  'legend-stone',
  'legend-hand',
  'legend-under',
  'legend-heirs',
  'legend-thief',
  'legend-title',
  'pact-boat',
  'pact-stone',
  'pact-near',
  'pact-palm',
  'pact-white',
  // The plains opening (data/plainsOpening.ts, C1–C7).
  'story-rain',
  'story-stele-night',
  'story-thrown',
  'story-road',
  'story-town-lights',
  'story-lights',
  'story-flash-hilt',
  'story-flash-grooves',
  'story-flash-marked',
  'story-flash-back',
];

const PAPER = '#e6dcc4';
const PAPER_2 = '#d6c9ab';
const PAPER_3 = '#c3b493';
const INK = '#1c191d';
const INK_2 = '#3d373b';
const WASH = '#8d8478';
const CINNABAR = '#9a3226';
const CINNABAR_HI = '#c8513c';
/** Figures on the panels, against their size in the world. */
const FIGURE_SCALE = 0.5;

/** Figure frames the panels borrow (the base looks), and the player's own. */
export interface PanelCast {
  look(name: string): SpriteSet | undefined;
  player(): SpriteSet | undefined;
}

const hash = (x: number, y: number, s = 0) => {
  let h = (x * 374761393 + y * 668265263 + s * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};

let paperBase: HTMLCanvasElement | null = null;
/** Rice paper: a sparse fibre grain and a darker deckle edge, in hard steps. */
function paper(): HTMLCanvasElement {
  if (paperBase) return paperBase;
  const c = canvas(PANEL_W, PANEL_H);
  const g = ctx2d(c);
  g.fillStyle = PAPER;
  g.fillRect(0, 0, PANEL_W, PANEL_H);
  for (let y = 0; y < PANEL_H; y++)
    for (let x = 0; x < PANEL_W; x++) {
      const edge = Math.min(x, y, PANEL_W - 1 - x, PANEL_H - 1 - y);
      const r = hash(x, y, 1);
      if (edge < 2 || (edge < 5 && r < 0.5 - edge * 0.1)) {
        g.fillStyle = edge < 1 ? PAPER_3 : PAPER_2;
        g.fillRect(x, y, 1, 1);
      } else if (r < 0.018) {
        g.fillStyle = PAPER_2;
        g.fillRect(x, y, 1 + (hash(x, y, 2) < 0.3 ? 1 : 0), 1);
      }
    }
  return (paperBase = c);
}

class Brush {
  constructor(readonly g: CanvasRenderingContext2D) {}

  dot(x: number, y: number, c: string, w = 1, h = 1): void {
    this.g.fillStyle = c;
    this.g.fillRect(Math.round(x), Math.round(y), w, h);
  }

  disc(cx: number, cy: number, r: number, c: string): void {
    const g = this.g;
    g.fillStyle = c;
    const R = Math.max(0.5, r);
    for (let y = -Math.ceil(R); y <= Math.ceil(R); y++) {
      const half = Math.floor(Math.sqrt(Math.max(0, R * R - y * y)));
      if (R * R - y * y < 0) continue;
      g.fillRect(Math.round(cx) - half, Math.round(cy) + y, half * 2 + 1, 1);
    }
  }

  /** A brush stroke through points: swelling in the middle and tapering at both ends. */
  stroke(pts: readonly (readonly [number, number])[], w: number, c: string, taper = true): void {
    let total = 0;
    for (let i = 1; i < pts.length; i++)
      total += Math.hypot(pts[i]![0] - pts[i - 1]![0], pts[i]![1] - pts[i - 1]![1]);
    let run = 0;
    for (let i = 1; i < pts.length; i++) {
      const [x0, y0] = pts[i - 1]!;
      const [x1, y1] = pts[i]!;
      const len = Math.hypot(x1 - x0, y1 - y0);
      const n = Math.max(1, Math.ceil(len * 1.5));
      for (let k = 0; k <= n; k++) {
        const u = total > 0 ? (run + (len * k) / n) / total : 0;
        const r = taper
          ? (w / 2) * Math.max(0.35, Math.sin(Math.PI * Math.min(1, Math.max(0, u))) ** 0.6)
          : w / 2;
        if (r < 0.75) this.dot(x0 + ((x1 - x0) * k) / n, y0 + ((y1 - y0) * k) / n, c);
        else this.disc(x0 + ((x1 - x0) * k) / n, y0 + ((y1 - y0) * k) / n, r, c);
      }
      run += len;
    }
  }

  /** A wave line across the panel. */
  wave(y: number, amp: number, len: number, ph: number, c: string, x0 = 0, x1 = PANEL_W): void {
    const pts: [number, number][] = [];
    for (let x = x0; x <= x1; x += 2) pts.push([x, y + Math.sin((x / len) * Math.PI * 2 + ph) * amp]);
    this.stroke(pts, 2, c, false);
  }

  /** A dithered wash of a colour over a box (every other pixel, or every fourth when light). */
  wash(x: number, y: number, w: number, h: number, c: string, light = false): void {
    this.g.fillStyle = c;
    for (let j = 0; j < h; j++)
      for (let i = 0; i < w; i++) {
        const X = x + i;
        const Y = y + j;
        if (light ? X % 2 === 0 && Y % 2 === 0 : (X + Y) % 2 === 0) this.g.fillRect(X, Y, 1, 1);
      }
  }

  /** A blade standing point-down in stone: hilt, guard and as much blade as shows. */
  sword(x: number, y: number, blade: number, c = INK): void {
    this.dot(x, y - blade, c, 1, blade);
    this.dot(x - 2, y - blade - 1, c, 5, 1);
    this.dot(x, y - blade - 6, c, 1, 5);
    this.dot(x, y - blade - 7, CINNABAR, 1, 1);
  }

  /** The Nine Swords stone: a weathered block, nine grooves along its top. */
  stone(cx: number, base: number, w: number, h: number, grooves = true): number[] {
    const x0 = Math.round(cx - w / 2);
    const top = base - h;
    for (let y = top; y < base; y++) {
      const k = (y - top) / h;
      const inset = Math.round((1 - k) * 3 + (hash(0, y, 5) < 0.3 ? 1 : 0));
      this.dot(x0 + inset, y, INK_2, w - inset * 2, 1);
    }
    // Lit from the upper right: the left face in shadow, a bare paper edge on the right.
    this.wash(x0 + 3, top + 2, Math.round(w * 0.35), h - 3, INK);
    for (let y = top + 2; y < base - 1; y++) this.dot(x0 + w - 4 - Math.round(((base - y) / h) * 2), y, WASH);
    this.stroke(
      [
        [x0, base],
        [x0 + w, base],
      ],
      2,
      INK,
      false,
    );
    const xs: number[] = [];
    if (grooves)
      for (let i = 0; i < 9; i++) {
        const gx = Math.round(x0 + 6 + ((w - 12) * i) / 8);
        xs.push(gx);
        this.dot(gx - 1, top + 1, PAPER_3, 3, 2);
        this.dot(gx, top + 1, INK, 1, 3);
      }
    return xs;
  }

  /** A rig frame filled with ink, its feet at (x, y). */
  figure(
    set: SpriteSet | undefined,
    frame: (s: SpriteSet) => HTMLCanvasElement | undefined,
    x: number,
    y: number,
    flip = false,
    c = INK,
  ): void {
    if (!set) return;
    const img = frame(set);
    if (!img) return;
    // The world's figures stand some 80 art pixels tall: on a panel they are drawn at half that.
    const k = FIGURE_SCALE;
    const w = Math.max(1, Math.round(artW(img) * k));
    const h = Math.max(1, Math.round(artH(img) * k));
    const sil = canvas(w, h);
    const s = ctx2d(sil);
    s.imageSmoothingEnabled = false;
    s.drawImage(img, 0, 0, w, h);
    s.globalCompositeOperation = 'source-in';
    s.fillStyle = c;
    s.fillRect(0, 0, w, h);
    const dx = Math.round((artX(img) - set.ax) * k);
    const dy = Math.round((artY(img) - set.ay) * k);
    const g = this.g;
    g.save();
    if (flip) {
      g.translate(Math.round(x), 0);
      g.scale(-1, 1);
      g.drawImage(sil, dx, Math.round(y) + dy);
    } else g.drawImage(sil, Math.round(x) + dx, Math.round(y) + dy);
    g.restore();
  }

  /** An open hand seen from above: palm and five fingers. */
  hand(cx: number, cy: number, s: number, c: string): void {
    this.disc(cx, cy, 5 * s, c);
    for (let i = 0; i < 4; i++)
      this.stroke(
        [
          [cx - 4 * s + i * 2.6 * s, cy - 3 * s],
          [cx - 5 * s + i * 2.9 * s, cy - 12 * s + Math.abs(i - 1.5) * 1.5 * s],
        ],
        2.4 * s,
        c,
        false,
      );
    this.stroke(
      [
        [cx + 4 * s, cy],
        [cx + 10 * s, cy - 4 * s],
      ],
      2.6 * s,
      c,
      false,
    );
  }
}

/** Rain over a panel: short slanted streaks falling with `age`. */
function rain(b: Brush, age: number, c: string, n: number): void {
  for (let i = 0; i < n; i++) {
    const x0 = hash(i, 11) * (PANEL_W + 30);
    const y = (hash(i, 12) * PANEL_H + age * (90 + hash(i, 13) * 40)) % (PANEL_H + 10) - 6;
    const x = x0 - y * 0.3;
    b.dot(x, y, c, 1, 1);
    b.dot(x - 1, y + 2, c, 1, 2);
  }
}

/** A walled town on the horizon: roofs and a gate tower in ink. */
function town(b: Brush): void {
  b.dot(30, 58, INK, 132, 8);
  for (const [x, w, hgt] of [
    [36, 22, 8],
    [62, 18, 6],
    [86, 20, 12],
    [112, 24, 7],
    [140, 18, 9],
  ] as const) {
    b.stroke(
      [
        [x - 2, 58],
        [x + w / 2, 58 - hgt],
        [x + w + 2, 58],
      ],
      2,
      INK,
      false,
    );
    b.dot(x, 58 - hgt / 2, INK, w, hgt / 2);
  }
}

/** A seal stamp in the corner, cinnabar on paper. */
function seal(b: Brush): void {
  const x = PANEL_W - 18;
  const y = PANEL_H - 18;
  b.dot(x, y, CINNABAR, 9, 9);
  b.dot(x + 2, y + 2, PAPER, 5, 1);
  b.dot(x + 2, y + 4, PAPER, 1, 3);
  b.dot(x + 6, y + 4, PAPER, 1, 3);
  b.dot(x + 2, y + 6, PAPER, 5, 1);
}

/**
 * Draws panel `art` at `age` seconds into it onto `g` (PANEL_W × PANEL_H). `ink` darkens the
 * paper for the night panels.
 */
export function drawPanel(g: CanvasRenderingContext2D, art: string, age: number, cast: PanelCast): void {
  g.imageSmoothingEnabled = false;
  g.drawImage(paper(), 0, 0);
  const b = new Brush(g);
  const W = PANEL_W;
  const H = PANEL_H;
  const hero = cast.look('hero');
  switch (art) {
    case 'legend-ink': {
      // One brushed circle (an ensō), left open where the stroke lifted.
      const pts: [number, number][] = [];
      const grow = Math.min(1, age / 2.5);
      for (let a = 0; a <= Math.PI * 1.85 * grow; a += 0.08)
        pts.push([W / 2 + Math.cos(a - 2) * 30, H / 2 + Math.sin(a - 2) * 30]);
      if (pts.length > 1) b.stroke(pts, 5, INK);
      seal(b);
      break;
    }
    case 'legend-waves': {
      for (let i = 0; i < 4; i++)
        b.wave(64 + i * 11, 2 + i, 26 + i * 6, age * (1.2 - i * 0.2) + i, i % 2 ? INK_2 : INK, 0, W);
      b.stroke(
        [
          [10, 66],
          [40, 62],
          [62, 66],
        ],
        6,
        INK,
      );
      b.figure(hero, (s) => (Math.floor(age * 1.5) % 2 ? s.poses.cut : s.atk) ?? s.atk, 38, 62);
      if (Math.floor(age * 1.5) % 2)
        b.stroke(
          [
            [44, 30],
            [62, 40],
            [66, 56],
          ],
          2,
          INK_2,
        );
      break;
    }
    case 'legend-bandits': {
      b.stroke(
        [
          [0, 82],
          [W, 82],
        ],
        2,
        INK_2,
        false,
      );
      b.figure(hero, (s) => s.poses.cut ?? s.atk, W / 2, 82);
      const bandit = cast.look('bandit');
      const fallen = Math.min(3, Math.floor((age - 1.5) / 1) + 1);
      for (let i = 0; i < 3; i++) {
        const x = [52, 140, 160][i]!;
        if (i < fallen) b.figure(bandit, (s) => s.sit, x, 84, i > 0);
        else b.figure(bandit, (s) => s.atk, x, 82, i > 0);
      }
      break;
    }
    case 'legend-swords': {
      const n = Math.min(8, Math.floor(age * 1.6) + 1);
      for (let i = 0; i < n; i++) {
        const a = Math.PI * (1.15 + (0.7 * i) / 7);
        const r = 42;
        const x = W / 2 + Math.cos(a) * r;
        const y = 78 + Math.sin(a) * r * 0.9;
        b.stroke(
          [
            [x, y],
            [x + Math.cos(a) * 18, y + Math.sin(a) * 16],
          ],
          2,
          INK,
          false,
        );
        b.dot(x + Math.cos(a) * 18 - 2, y + Math.sin(a) * 16, INK, 5, 1);
      }
      b.figure(cast.look('boss') ?? hero, (s) => s.idle[0], W / 2, 86);
      break;
    }
    case 'legend-stone': {
      const xs = b.stone(W / 2, 92, 96, 34);
      const n = Math.min(8, Math.floor(age / 1.2) + 1);
      for (let i = 0; i < n; i++) b.sword(xs[i]!, 59, 6 + (i % 3));
      b.figure(cast.look('elder'), (s) => s.idle[0], 36, 94);
      break;
    }
    case 'legend-hand': {
      const xs = b.stone(W / 2, 104, 150, 50);
      for (let i = 0; i < 8; i++) b.sword(xs[i]!, 55, 10);
      const down = Math.min(1, age / 1.5);
      b.hand(xs[8]! + 2, 30 + down * 14, 1.1, INK);
      break;
    }
    case 'legend-under': {
      // Cross-section: the stone above ground, the black sword sealed in the dark beneath it.
      const xs = b.stone(W / 2, 46, 80, 22);
      for (let i = 0; i < 9; i++) b.sword(xs[i]!, 25, 5);
      b.wash(0, 48, W, H - 48, INK_2);
      for (const x of xs) b.dot(x, 48, INK, 1, 22);
      const beat = age % 1.4 < 0.25;
      b.stroke(
        [
          [W / 2 - 44, 80],
          [W / 2 + 44, 80],
        ],
        5,
        '#060506',
        false,
      );
      b.dot(W / 2 - 50, 78, '#060506', 7, 5);
      b.dot(W / 2 + 46, 79, CINNABAR, 3, 3);
      if (beat)
        for (let i = 0; i < 18; i++) b.dot(W / 2 - 40 + hash(i, 3) * 80, 74 + hash(i, 4) * 12, CINNABAR_HI);
      break;
    }
    case 'legend-heirs': {
      const xs = b.stone(W / 2, 96, 96, 30);
      const gone = Math.min(8, Math.floor(age / 0.7));
      for (let i = 0; i < 8; i++) {
        if (i >= gone) b.sword(xs[i]!, 67, 7);
        else {
          // Each blade carried off to its own land: a stroke flying out to the edge.
          const k = Math.min(1, (age - i * 0.7) / 1.2);
          const tx = i < 4 ? 8 + i * 12 : W - 8 - (i - 4) * 12;
          const ty = 14 + (i % 4) * 6;
          b.stroke(
            [
              [xs[i]!, 60],
              [xs[i]! + (tx - xs[i]!) * k, 60 + (ty - 60) * k],
            ],
            1.6,
            INK_2,
          );
        }
      }
      break;
    }
    case 'legend-thief': {
      // Night: the paper washed with ink, the black sword drawn out from under the stone.
      b.wash(0, 0, W, H, INK_2);
      const xs = b.stone(W / 2 + 20, 90, 80, 26);
      b.sword(xs[8]!, 64, 6);
      const up = Math.min(1, Math.max(0, (age - 1) / 3));
      b.figure(cast.look('boss'), (s) => s.cast, W / 2 - 40, 92, false, '#060506');
      b.stroke(
        [
          [W / 2 - 26, 70 - up * 22],
          [W / 2 - 26, 92 - up * 22],
        ],
        3,
        '#060506',
        false,
      );
      b.dot(W / 2 - 27, 69 - up * 22, CINNABAR_HI, 3, 2);
      if (age > 5)
        b.stroke(
          [
            [W / 2 + 10, 91],
            [W / 2 + 22, 98],
            [W / 2 + 18, 106],
          ],
          2,
          CINNABAR,
        );
      break;
    }
    case 'legend-title': {
      b.stone(W / 2, 80, 70, 24, false);
      for (let i = 0; i < 9; i++) b.dot(W / 2 - 24 + i * 6, 52, i === 8 ? CINNABAR : INK, 2, 2);
      seal(b);
      break;
    }
    case 'pact-boat': {
      b.disc(150, 22, 9, PAPER_2);
      b.disc(147, 20, 8, PAPER);
      for (let i = 0; i < 3; i++) b.wave(74 + i * 9, 1, 30, age * 0.6 + i * 2, i === 0 ? INK : INK_2, 0, W);
      b.stroke(
        [
          [70, 70],
          [80, 76],
          [118, 76],
          [128, 68],
        ],
        4,
        INK,
        false,
      );
      b.figure(cast.player(), (s) => s.sit, 100, 72, true);
      break;
    }
    case 'pact-stone': {
      const xs = b.stone(W / 2, 92, 110, 40);
      for (let i = 0; i < 8; i++) b.sword(xs[i]!, 53, 8);
      // The grooves glowing red as blood, and the air shimmering above them.
      const glow = 0.5 + 0.5 * Math.sin(age * 4);
      for (const x of xs) b.dot(x, 53, glow > 0.5 ? CINNABAR_HI : CINNABAR, 1, 3);
      for (let i = 0; i < 26; i++) {
        const x = 40 + hash(i, 7) * 110;
        const y = 40 - ((age * 8 + hash(i, 8) * 30) % 30);
        b.dot(x + Math.sin(age * 5 + i) * 1.5, y, i % 3 ? PAPER_3 : CINNABAR);
      }
      break;
    }
    case 'pact-near': {
      // Close on the stone's top: eight hilts and the ninth groove, empty.
      b.dot(0, 58, INK_2, W, H - 58);
      b.wash(0, 58, W, H - 58, INK);
      for (let i = 0; i < 9; i++) {
        const x = 12 + i * 21;
        b.dot(x - 2, 54, INK, 6, 6);
        if (i < 8) {
          b.dot(x, 20, INK, 2, 34);
          b.dot(x - 4, 34, INK, 10, 2);
          b.dot(x, 18, CINNABAR, 2, 2);
        } else {
          const glow = 0.5 + 0.5 * Math.sin(age * 3);
          b.dot(x - 1, 55, glow > 0.4 ? CINNABAR_HI : CINNABAR, 4, 4);
        }
      }
      break;
    }
    case 'pact-palm': {
      b.dot(0, 62, INK_2, W, H - 62);
      b.wash(0, 62, W, H - 62, INK);
      b.dot(W / 2 - 4, 58, INK, 8, 8);
      const down = Math.min(1, age / 0.4);
      b.hand(W / 2, 24 + down * 30, 1.6, INK);
      if (age > 0.3)
        for (let i = 0; i < 5; i++) b.dot(W / 2 - 6 + i * 3, 66 + ((age * 6 + i * 3) % 18), CINNABAR, 1, 2);
      break;
    }
    case 'story-rain': {
      // Night and rain: the page all but black, the stone's shape and one red groove in it.
      b.dot(0, 0, INK, W, H);
      b.wash(0, 0, W, H, INK_2, true);
      b.stone(W / 2, 92, 96, 32);
      const glow = 0.5 + 0.5 * Math.sin(age * 2.4);
      b.dot(W / 2 + 41, 61, glow > 0.5 ? CINNABAR_HI : CINNABAR, 2, 2);
      rain(b, age, PAPER_3, 70);
      break;
    }
    case 'story-stele-night': {
      // The brother at the stone in the rain, his palm on the ninth groove; the groove burns.
      b.dot(0, 0, INK_2, W, H);
      b.wash(0, 0, W, H, INK, true);
      const xs = b.stone(W / 2 + 10, 96, 100, 34);
      for (let i = 0; i < 8; i++) b.sword(xs[i]!, 62, 7, INK);
      const gx = xs[8]!;
      const burn = Math.min(1, age / 1.5);
      b.disc(gx, 62, 2 + burn * 4, burn > 0.6 ? CINNABAR_HI : CINNABAR);
      b.figure(hero, (s) => s.cast, gx + 14, 96, true, '#060506');
      rain(b, age, WASH, 50);
      break;
    }
    case 'story-thrown': {
      // The groove flares and throws him back; on the paving he writes with a bleeding hand.
      b.dot(0, 0, INK_2, W, H);
      b.wash(0, 0, W, H, INK, true);
      b.stone(150, 70, 64, 26);
      const flare = Math.max(0, 1 - age / 1.2);
      if (flare > 0) b.disc(176, 44, 4 + flare * 14, CINNABAR_HI);
      b.disc(176, 44, 3, CINNABAR);
      b.figure(hero, (s) => s.sit, 60, 98, false, '#060506');
      // The note on the stones, and the red drops on it.
      b.dot(78, 92, PAPER, 18, 10);
      const strokes = Math.min(3, Math.floor(age / 0.8));
      for (let i = 0; i < strokes; i++) b.dot(81 + i * 5, 94, INK, 2, 6);
      for (let i = 0; i < 4; i++) b.dot(74 + i * 6, 102 + (i % 2), CINNABAR, 1, 1);
      rain(b, age, WASH, 40);
      break;
    }
    case 'story-road': {
      // The south road at night in the rain: the hero with a lantern, reading the worn letter.
      b.dot(0, 0, INK_2, W, H);
      b.wash(0, 0, W, H, INK, true);
      b.stroke(
        [
          [40, H],
          [92, 52],
        ],
        3,
        WASH,
        false,
      );
      b.stroke(
        [
          [150, H],
          [100, 52],
        ],
        3,
        WASH,
        false,
      );
      b.figure(cast.player(), (s) => s.idle[0], 96, 96, false, '#060506');
      const flick = hash(Math.floor(age * 8), 3) < 0.5 ? CINNABAR_HI : '#e8b25a';
      b.disc(110, 74, 3, flick);
      b.dot(110, 70, INK, 1, 2);
      b.dot(86, 70, PAPER, 6, 5);
      rain(b, age, WASH, 60);
      break;
    }
    case 'story-town-lights': {
      // The town ahead: roofs and a few warm windows down the road.
      b.dot(0, 0, INK_2, W, H);
      b.wash(0, 0, W, H, INK, true);
      town(b);
      for (let i = 0; i < 7; i++) b.dot(52 + i * 15, 54 + (i % 3), hash(i, Math.floor(age * 2)) < 0.8 ? '#e8b25a' : CINNABAR, 1, 1);
      b.figure(cast.player(), (s) => s.walk[0] ?? s.idle[0], 96, 104, true, '#060506');
      rain(b, age, WASH, 40);
      break;
    }
    case 'story-lights': {
      // Red lights burst from the town in every direction; one turns toward the hero.
      b.dot(0, 0, INK_2, W, H);
      b.wash(0, 0, W, H, INK, true);
      town(b);
      const k = Math.min(1, age / 1.4);
      for (let i = 0; i < 9; i++) {
        const a = -Math.PI * (0.08 + (0.84 * i) / 8);
        const r = 10 + k * 70;
        b.stroke(
          [
            [W / 2, 50],
            [W / 2 + Math.cos(a) * r, 50 + Math.sin(a) * r * 0.6],
          ],
          1.5,
          CINNABAR,
          false,
        );
        b.disc(W / 2 + Math.cos(a) * r, 50 + Math.sin(a) * r * 0.6, 1.5, CINNABAR_HI);
      }
      // The one that comes: it grows on its way down to the hero.
      const c = Math.min(1, Math.max(0, (age - 0.8) / 1.5));
      b.disc(W / 2 + c * 40, 50 + c * 46, 2 + c * 7, CINNABAR_HI);
      b.figure(cast.player(), (s) => s.idle[0], W / 2 + 46, 106, true, '#060506');
      break;
    }
    case 'story-flash-hilt': {
      // A hand on a hilt.
      b.dot(W / 2 - 2, 10, INK, 4, 70);
      b.dot(W / 2 - 14, 64, INK, 28, 4);
      b.hand(W / 2 + 2, 48, 1.4, INK_2);
      b.dot(W / 2 - 1, 80, CINNABAR, 2, 12);
      break;
    }
    case 'story-flash-grooves': {
      // The nine grooves, all burning.
      for (let i = 0; i < 9; i++) {
        const x = 16 + i * 20;
        b.dot(x - 3, 50, INK, 8, 10);
        b.dot(x - 1, 52, i === 8 ? CINNABAR_HI : CINNABAR, 4, 6);
      }
      break;
    }
    case 'story-flash-marked': {
      // Strangers, each with the red mark in the palm.
      for (let i = 0; i < 4; i++) {
        const x = 36 + i * 40;
        b.figure(i % 2 ? cast.look('bandit') : hero, (s) => s.idle[0], x, 96, i % 2 === 1);
        b.disc(x + (i % 2 ? -8 : 8), 70, 2, CINNABAR_HI);
      }
      break;
    }
    case 'story-flash-back': {
      // The brother's back, walking north into the rain.
      b.figure(hero, (s) => s.walk[0] ?? s.idle[0], W / 2, 90, false, INK);
      rain(b, age, INK_2, 50);
      break;
    }
    case 'pact-white': {
      g.fillStyle = '#f6f1e6';
      g.fillRect(0, 0, W, H);
      const r = 8 + age * 14;
      for (let a = 0; a < Math.PI * 2; a += 0.05)
        b.dot(W / 2 + Math.cos(a) * r, H / 2 + Math.sin(a) * r * 0.8, CINNABAR_HI);
      break;
    }
  }
}
